import { useState } from "react";
import { Button, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import {
  FaArrowUp,
  FaCalendarAlt,
  FaCreditCard,
  FaHistory
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Menu from "../../components/Menu";
import NavbarDashboard from "../../components/NavbarDashboard";
import { CancelSubscriptionModal } from "../../components/Subscriptions/CancelSubscriptionModal";
import "../../components/Subscriptions/Subscriptions.css";
import { SubscriptionStatus } from "../../components/Subscriptions/SubscriptionStatus";
import { useSubscription } from "../../hooks/useSubscription";
import { PaymentHistoryModal } from "../PaymentHistory";

/**
 * Página de gerenciamento de assinatura
 */
export const MySubscriptionPage = () => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const navigate = useNavigate();

  const { currentSubscription, isLoading, cancel, isCancelling } =
    useSubscription();

  const planNames = {
    1: "Free",
    2: "Start",
    3: "Gold",
    4: "Premium",
    5: "Custom",
  };

  if (isLoading) {
    return (
      <>
        <NavbarDashboard />
        <section className="home">
          <Menu />
          <Container className="py-5 text-center">
            <Spinner animation="border" variant="warning" />
            <p className="mt-3">Carregando informações da assinatura...</p>
          </Container>
        </section>
      </>
    );
  }

  const subscription = currentSubscription?.subscription;
  const isOnTrial = currentSubscription?.is_on_trial;
  const trialEndsAt = currentSubscription?.trial_ends_at;
  const isPendingCancellation = currentSubscription?.is_pending_cancellation;
  const accessUntil = currentSubscription?.access_until;

  if (!subscription) {
    return (
      <>
        <NavbarDashboard />
        <section className="home">
          <Menu />
          <Container className="py-5">
            <Card className="text-center shadow">
              <Card.Body className="py-5">
                <FaCreditCard size={64} className="text-muted mb-4" />
                <h3>Você não possui assinatura ativa</h3>
                <p className="text-muted mb-4">
                  Escolha um plano para desbloquear todos os recursos da
                  plataforma
                </p>
                <Button
                  className="d-flex justify-content-center shadow-sm fs-6 p-2 rounded-2 fw-normal text-dark borderColor"
                  style={{ backgroundColor: "#DAA520", justifySelf: "center" }}
                  onClick={() => navigate("/planos")}
                >
                  Ver Planos Disponíveis
                </Button>
              </Card.Body>
            </Card>
          </Container>
        </section>
      </>
    );
  }

  const planName = planNames[subscription.plan_id];

  return (
    <>
      <NavbarDashboard />
      <section className="home">
        <Menu />
        <div style={{ padding: "2rem", margin: "0 auto" }}>
          {/* Header Section */}
          <div
            className="subscription-header text-center mb-4"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              padding: "2.5rem",
              borderRadius: "16px",
              color: "white",
              boxShadow: "0 10px 30px rgba(102, 126, 234, 0.2)",
            }}
          >
            <h1 className="fw-bold mb-3">Minha Assinatura</h1>
            <p
              className="lead mb-3"
              style={{ opacity: 0.95, fontSize: "1.1rem" }}
            >
              Gerencie sua assinatura e recursos
            </p>
            <Button
              variant="light"
              size="lg"
              onClick={() => navigate("/planos")}
              style={{
                borderRadius: "12px",
                padding: "0.75rem 2rem",
                fontWeight: "600",
              }}
            >
              Ver Outros Planos
            </Button>
          </div>

          <Row className="g-4">
            <Col lg={8}>
              {/* Status da Assinatura */}
              <SubscriptionStatus
                subscription={subscription}
                isOnTrial={isOnTrial}
                trialEndsAt={trialEndsAt}
                isPendingCancellation={isPendingCancellation}
                accessUntil={accessUntil}
              />

              {/* Informações do Plano */}
              <Card
                className="mb-4 border-0"
                style={{
                  borderRadius: "16px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                <Card.Header
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    borderRadius: "16px 16px 0 0",
                    padding: "1.5rem",
                  }}
                >
                  <h4 className="mb-0 fw-bold">Plano {planName}</h4>
                </Card.Header>
                <Card.Body style={{ padding: "2rem" }}>
                  <Row className="mb-4">
                    <Col md={6}>
                      <div
                        className="value-badge"
                        style={{
                          background: "#e7f3ff",
                          borderRadius: "12px",
                          padding: "1.5rem",
                          border: "2px solid #007bff",
                          textAlign: "center",
                        }}
                      >
                        <div>
                          <div
                            className="price-text"
                            style={{
                              fontSize: "2.5rem",
                              fontWeight: "700",
                              color: "#007bff",
                              margin: "0.5rem 0",
                            }}
                          >
                            R$ {parseFloat(subscription.value).toFixed(2)}
                          </div>
                          <small className="text-muted fw-semibold">
                            por mês
                          </small>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div
                        style={{
                          background: "#f8f9fa",
                          borderRadius: "12px",
                          padding: "1.5rem",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                      >
                        <div className="mb-3">
                          <FaCalendarAlt
                            className="text-primary me-2"
                            size={20}
                          />
                          <strong>Próximo vencimento</strong>
                          <div className="mt-2 fs-5 fw-semibold text-primary">
                            {new Date(
                              subscription.next_due_date
                            ).toLocaleDateString("pt-BR", {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                            })}
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <Row className="mb-4" style={{ fontSize: "0.95rem" }}>
                    <Col md={6}>
                      <div
                        style={{
                          padding: "1rem",
                          background: "#f8f9fa",
                          borderRadius: "8px",
                        }}
                      >
                        <strong className="d-block mb-2">
                          📅 Assinatura desde:
                        </strong>
                        <span className="text-muted">
                          {new Date(subscription.created_at).toLocaleDateString(
                            "pt-BR",
                            {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                            }
                          )}
                        </span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div
                        style={{
                          padding: "1rem",
                          background: "#f8f9fa",
                          borderRadius: "8px",
                        }}
                      >
                        <strong className="d-block mb-2">
                          🎯 ID da Assinatura:
                        </strong>
                        <code
                          style={{
                            background: "white",
                            padding: "0.25rem 0.5rem",
                            borderRadius: "4px",
                            fontSize: "0.85rem",
                          }}
                        >
                          {subscription.asaas_subscription_id ||
                            subscription.id}
                        </code>
                      </div>
                    </Col>
                  </Row>

                  <div
                    className="d-flex gap-3 flex-wrap"
                    style={{ marginTop: "2rem" }}
                  >
                    {subscription.plan_id < 4 &&
                      subscription.status === "active" && (
                        <Button
                          variant="success"
                          size="lg"
                          onClick={() => navigate("/planos")}
                          style={{
                            borderRadius: "12px",
                            padding: "0.75rem 2rem",
                            fontWeight: "600",
                            boxShadow: "0 4px 12px rgba(40, 167, 69, 0.3)",
                          }}
                        >
                          <FaArrowUp className="me-2" />
                          Fazer Upgrade
                        </Button>
                      )}
                    {subscription.status === "active" && !isPendingCancellation && (
                      <Button
                        variant="outline-danger"
                        size="lg"
                        onClick={() => setShowCancelModal(true)}
                        style={{
                          borderRadius: "12px",
                          padding: "0.75rem 2rem",
                          fontWeight: "600",
                          borderWidth: "2px",
                        }}
                      >
                        Cancelar Assinatura
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Sidebar com Ações Rápidas */}
            <Col lg={4}>
              <Card
                className="mb-4 action-card border-0"
                style={{
                  borderRadius: "16px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                <Card.Header
                  style={{
                    background:
                      "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                    color: "white",
                    borderRadius: "16px 16px 0 0",
                    padding: "1.25rem 1.5rem",
                  }}
                >
                  <h5 className="mb-0 fw-bold">⚡ Ações Rápidas</h5>
                </Card.Header>
                <Card.Body style={{ padding: "1.5rem" }}>
                  <div className="d-grid gap-3">
                    <Button
                      variant="outline-primary"
                      size="lg"
                      onClick={() => setShowPaymentHistoryModal(true)}
                      style={{
                        borderRadius: "12px",
                        padding: "0.875rem",
                        fontWeight: "500",
                        borderWidth: "2px",
                        textAlign: "left",
                      }}
                    >
                      <FaHistory className="me-2" size={18} />
                      Ver Histórico de Pagamentos
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              <Card
                className="border-0"
                style={{
                  borderRadius: "16px",
                  background:
                    "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
                  boxShadow: "0 4px 12px rgba(252, 182, 159, 0.3)",
                }}
              >
                <Card.Body style={{ padding: "1.5rem" }}>
                  <h6 className="fw-bold mb-3" style={{ fontSize: "1.1rem" }}>
                    👋 Precisa de ajuda?
                  </h6>
                  <p
                    className="mb-3"
                    style={{ fontSize: "0.95rem", opacity: 0.9 }}
                  >
                    Entre em contato com nosso suporte para esclarecer dúvidas
                    sobre sua assinatura.
                  </p>
                  <a
                    href="https://api.whatsapp.com/send?l=pt_br&amp;phone=+5551992542818&amp;text=Gostaria de suporte sobre o minha assinatura no EASYVOG!"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button
                      variant="dark"
                      className="w-100"
                      style={{
                        borderRadius: "12px",
                        padding: "0.75rem",
                        fontWeight: "600",
                      }}
                    >
                      Falar com suporte →
                    </Button>
                  </a>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Modal de Cancelamento */}
          <CancelSubscriptionModal
            show={showCancelModal}
            onHide={() => setShowCancelModal(false)}
            onConfirm={() => {
              cancel();
              setShowCancelModal(false);
            }}
            isLoading={isCancelling}
            planName={planName}
          />

          {/* Modal de Histórico de Pagamentos */}
          <PaymentHistoryModal
            show={showPaymentHistoryModal}
            onHide={() => setShowPaymentHistoryModal(false)}
          />
        </div>
      </section>
    </>
  );
};
